import React, { useState } from 'react';
import { Accordion, Alert, Badge, Button, Card, Col, Row } from 'react-bootstrap';
import {
    FiBriefcase,
    FiCalendar,
    FiCreditCard,
    FiEye,
    FiEyeOff,
    FiGlobe,
    FiHash,
    FiHome,
    FiInfo,
    FiMail,
    FiMapPin,
    FiPhone,
    FiShield,
    FiUser,
} from 'react-icons/fi';
import './ClientInfoSections.css';

const Label = ({ children, icon: Icon }) => (
    <div className="client-info-sections__label">
        {Icon ? <Icon className="me-1" style={{ verticalAlign: '-0.1em' }} aria-hidden="true" /> : null}
        {children}
    </div>
);

const Value = ({ children }) => (
    <div className="client-info-sections__value">{children}</div>
);

const Field = ({ md = 6, icon, label, children }) => (
    <Col md={md} className="mb-3">
        <Label icon={icon}>{label}</Label>
        <Value>{children}</Value>
    </Col>
);

const GroupTitle = ({ children }) => (
    <h6 className="client-info-sections__group-title">{children}</h6>
);

const ClientAccordionItem = ({ eventKey, title, badge, children }) => (
    <Accordion.Item eventKey={eventKey} className="mb-2 border rounded overflow-hidden">
        <Accordion.Header>
            <div className="d-flex align-items-center justify-content-between w-100 pe-3">
                <span className="fw-semibold">{title}</span>
                {badge}
            </div>
        </Accordion.Header>
        <Accordion.Body>{children}</Accordion.Body>
    </Accordion.Item>
);

const isEmptyValue = (value) => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed === '' || trimmed.toLowerCase() === 'unknown' || trimmed.toLowerCase() === 'n/a';
    }
    return false;
};

const formatDisplayDate = (value) => {
    if (isEmptyValue(value)) return null;
    const raw = String(value).trim();
    const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) {
        const [, y, m, d] = iso;
        return `${d}/${m}/${y}`;
    }
    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) {
        return parsed.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    }
    return raw;
};

const fullName = (person) => {
    if (!person) return null;
    const name = [person.first_name, person.last_name]
        .filter((part) => !isEmptyValue(part))
        .map((part) => String(part).trim())
        .join(' ');
    return name || null;
};

const statusBadgeVariant = (status) => {
    const normalized = String(status || '').trim().toLowerCase();
    if (normalized === 'active' || normalized === 'actif') return 'success';
    if (normalized === 'inactive' || normalized === 'inactif') return 'secondary';
    if (normalized === 'suspended' || normalized === 'suspendu') return 'warning';
    if (normalized === 'blocked' || normalized === 'bloqué' || normalized === 'bloque') return 'danger';
    return 'primary';
};

const MissingValue = () => (
    <span className="client-info-sections__missing">Non renseigné</span>
);

const displayText = (value) => {
    if (isEmptyValue(value)) return <MissingValue />;
    return String(value).trim();
};

const displayBoolean = (value) => {
    if (value === null || value === undefined) return <MissingValue />;
    return (
        <Badge bg={value ? 'success' : 'secondary'}>
            {value ? 'Oui' : 'Non'}
        </Badge>
    );
};

const resolveClientAddress = (client) => {
    const candidates = [client?.client_address, client?.address, client?.adress];
    const found = candidates.find((value) => !isEmptyValue(value));
    return found ? String(found).trim() : null;
};

const maskIban = (iban) => {
    if (!iban || iban === '—') return iban;
    const cleanedIban = String(iban).replace(/\s/g, '');
    if (cleanedIban.length < 7) return iban;
    const prefix = cleanedIban.substring(0, 4);
    const suffix = cleanedIban.substring(cleanedIban.length - 2);
    const middleLength = cleanedIban.length - 6;
    return `${prefix}${'*'.repeat(middleLength)}${suffix}`;
};

const getFullIban = (client) => {
    if (isEmptyValue(client?.iban)) return null;
    return String(client.iban).trim();
};

const getMaskedIban = (client) => {
    if (!isEmptyValue(client?.iban_masked)) return String(client.iban_masked).trim();
    const fullIban = getFullIban(client);
    if (fullIban) return maskIban(fullIban);
    return null;
};

const hasIbanData = (client) => Boolean(getFullIban(client) || getMaskedIban(client));

const countMissingFields = (client, fields) => (
    fields.filter(({ getValue }) => isEmptyValue(getValue(client))).length
);

/** Motif de différence : Non renseigné si pas de titulaire ; sinon valeur API. */
const displayReasonOfDifference = (client) => {
    if (!client || isEmptyValue(client.account_holder_name)) {
        return <MissingValue />;
    }
    return displayText(client.reason_of_difference);
};

const displayOtherReasonComment = (client) => {
    if (!client || isEmptyValue(client.account_holder_name)) {
        return <MissingValue />;
    }
    return displayText(client.other_reason_comment);
};

/**
 * Card unique : Identité toujours visible.
 * Contact / Adresse / Naissance / Bancaire / Conformité en accordéon (ouvrir / fermer).
 */
const ClientInfoSections = ({
    clientInfo,
    partnerInfo = null,
    submissionDateFallback = null,
    showCompletenessAlert = true,
    className = 'mb-3',
    idPrefix = 'client',
}) => {
    const [showIban, setShowIban] = useState(false);
    const [activeKey, setActiveKey] = useState(null);

    const cardTitle = (clientName, status) => (
        <span className="d-inline-flex align-items-center flex-wrap gap-2">
            <FiUser className="me-1" aria-hidden="true" />
            Informations principales du client
            {clientName ? <span className="text-muted fw-normal">— {clientName}</span> : null}
            {!isEmptyValue(status) && (
                <Badge bg={statusBadgeVariant(status)}>{status}</Badge>
            )}
        </span>
    );

    if (!clientInfo || typeof clientInfo !== 'object') {
        return (
            <Card className={`client-info-sections ${className}`.trim()}>
                <Card.Header style={{ fontWeight: 600 }}>
                    {cardTitle(null, null)}
                </Card.Header>
                <Card.Body>
                    <p className="text-muted mb-0">Aucune information client disponible.</p>
                </Card.Body>
            </Card>
        );
    }

    const clientName = fullName(clientInfo);
    const clientBirthDate = formatDisplayDate(clientInfo.birth_date);
    const clientJoinedDate = formatDisplayDate(clientInfo.date_joined);
    const submissionRaw = !isEmptyValue(clientInfo.submission_date)
        ? clientInfo.submission_date
        : submissionDateFallback;
    const clientSubmissionDate = formatDisplayDate(submissionRaw);
    const clientAddress = resolveClientAddress(clientInfo);
    const partnerCompanyName = partnerInfo && !isEmptyValue(partnerInfo.company_name)
        ? partnerInfo.company_name
        : null;

    const missingProfileCount = showCompletenessAlert
        ? countMissingFields(clientInfo, [
            { key: 'email', getValue: (c) => c.email },
            { key: 'phone', getValue: (c) => c.phone_number },
            { key: 'birth_date', getValue: (c) => c.birth_date },
            { key: 'address', getValue: (c) => resolveClientAddress(c) },
            { key: 'city', getValue: (c) => c.city },
            { key: 'country', getValue: (c) => c.country },
            { key: 'iban', getValue: (c) => c.iban_masked || c.iban },
        ])
        : 0;

    return (
        <Card className={`client-info-sections ${className}`.trim()}>
            <Card.Header style={{ fontWeight: 600 }}>
                {cardTitle(clientName, clientInfo.status)}
            </Card.Header>
            <Card.Body>
                {missingProfileCount > 0 && (
                    <Alert variant="warning" className="mb-3 py-2">
                        <FiInfo className="me-2" aria-hidden="true" />
                        <strong>Informations incomplètes :</strong>{' '}
                        {missingProfileCount} champ(s) clé(s) non renseigné(s). Les valeurs manquantes sont
                        {' '}indiquées ci-dessous.
                    </Alert>
                )}

                <GroupTitle>Identité</GroupTitle>
                <Row>
                    <Field icon={FiUser} label="Prénom">
                        {displayText(clientInfo.first_name)}
                    </Field>
                    <Field icon={FiUser} label="Nom">
                        {displayText(clientInfo.last_name)}
                    </Field>
                    <Field icon={FiHash} label="Code client">
                        {displayText(clientInfo.client_code)}
                    </Field>
                    <Field icon={FiUser} label="Civilité">
                        {displayText(clientInfo.title)}
                    </Field>
                    <Field icon={FiBriefcase} label="Rôle">
                        {!isEmptyValue(clientInfo.role) ? (
                            <Badge bg="info">{clientInfo.role}</Badge>
                        ) : (
                            <MissingValue />
                        )}
                    </Field>
                    <Field icon={FiUser} label="Statut">
                        {!isEmptyValue(clientInfo.status) ? (
                            <Badge bg={statusBadgeVariant(clientInfo.status)}>{clientInfo.status}</Badge>
                        ) : (
                            <MissingValue />
                        )}
                    </Field>
                    <Field icon={FiCalendar} label="Date d'inscription">
                        {clientJoinedDate || <MissingValue />}
                    </Field>
                    <Field icon={FiCalendar} label="Date de soumission">
                        {clientSubmissionDate || <MissingValue />}
                    </Field>
                    <Field icon={FiBriefcase} label="Nom de l'entreprise (partenaire)">
                        {partnerCompanyName || <MissingValue />}
                    </Field>
                </Row>

                <Accordion
                    className="client-info-accordion mt-2"
                    activeKey={activeKey}
                    onSelect={(key) => setActiveKey(key)}
                >
                    <ClientAccordionItem
                        eventKey={`${idPrefix}-contact`}
                        title="Contact"
                    >
                        <Row>
                            <Field icon={FiMail} label="Email">
                                {displayText(clientInfo.email)}
                            </Field>
                            <Field icon={FiPhone} label="Téléphone">
                                {displayText(clientInfo.phone_number)}
                            </Field>
                            <Field icon={FiGlobe} label="Indicatif pays">
                                {displayText(clientInfo.country_code)}
                            </Field>
                        </Row>
                    </ClientAccordionItem>

                    <ClientAccordionItem
                        eventKey={`${idPrefix}-address`}
                        title="Adresse"
                    >
                        <Row>
                            <Field md={12} icon={FiHome} label="Adresse">
                                {clientAddress ? (
                                    <span style={{ whiteSpace: 'pre-line' }}>{clientAddress}</span>
                                ) : (
                                    <MissingValue />
                                )}
                            </Field>
                            <Field icon={FiMapPin} label="Ville">
                                {displayText(clientInfo.city)}
                            </Field>
                            <Field icon={FiMapPin} label="Code postal">
                                {displayText(clientInfo.zip_code)}
                            </Field>
                            <Field icon={FiGlobe} label="Pays">
                                {displayText(clientInfo.country)}
                            </Field>
                        </Row>
                    </ClientAccordionItem>

                    <ClientAccordionItem
                        eventKey={`${idPrefix}-birth`}
                        title="Naissance"
                    >
                        <Row>
                            <Field icon={FiCalendar} label="Date de naissance">
                                {clientBirthDate || <MissingValue />}
                            </Field>
                            <Field icon={FiMapPin} label="Lieu de naissance">
                                {displayText(clientInfo.place_of_birth)}
                            </Field>
                            <Field icon={FiMapPin} label="Ville de naissance">
                                {displayText(clientInfo.city_of_birth)}
                            </Field>
                            <Field icon={FiGlobe} label="Pays de naissance">
                                {displayText(clientInfo.country_of_birth)}
                            </Field>
                        </Row>
                    </ClientAccordionItem>

                    <ClientAccordionItem
                        eventKey={`${idPrefix}-banking`}
                        title="Coordonnées bancaires"
                        badge={
                            <Badge
                                bg={clientInfo.is_iban_received ? 'success' : 'secondary'}
                                pill
                                className="ms-2"
                            >
                                RIB {clientInfo.is_iban_received ? 'reçu' : 'non reçu'}
                            </Badge>
                        }
                    >
                        <Row>
                            <Field md={12} icon={FiUser} label="Titulaire du compte">
                                {displayText(clientInfo.account_holder_name)}
                            </Field>
                            <Field icon={FiCreditCard} label="IBAN">
                                {hasIbanData(clientInfo) ? (
                                    <span className="d-inline-flex align-items-center gap-2 flex-wrap">
                                        <code>
                                            {showIban && getFullIban(clientInfo)
                                                ? getFullIban(clientInfo)
                                                : getMaskedIban(clientInfo)}
                                        </code>
                                        {getFullIban(clientInfo) && (
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="client-info-sections__iban-toggle p-0"
                                                onClick={() => setShowIban((prev) => !prev)}
                                                title={showIban ? 'Masquer l\'IBAN' : 'Afficher l\'IBAN'}
                                                aria-label={showIban ? 'Masquer l\'IBAN' : 'Afficher l\'IBAN'}
                                            >
                                                {showIban ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                                            </Button>
                                        )}
                                    </span>
                                ) : (
                                    <MissingValue />
                                )}
                            </Field>
                            <Field icon={FiCreditCard} label="BIC">
                                {displayText(clientInfo.bic)}
                            </Field>
                            <Field icon={FiCreditCard} label="RIB reçu">
                                {displayBoolean(clientInfo.is_iban_received)}
                            </Field>
                        </Row>
                    </ClientAccordionItem>

                    <ClientAccordionItem
                        eventKey={`${idPrefix}-compliance`}
                        title="Conformité et consentements"
                    >
                        <Row>
                            <Field icon={FiShield} label="Vérification LCBFT client">
                                {displayBoolean(clientInfo.lcbft_checked)}
                            </Field>
                            <Field icon={FiShield} label="Vérification LCBFT RIB">
                                {displayBoolean(clientInfo.lcbft_checked_rib)}
                            </Field>
                            <Field icon={FiShield} label="Attestation pertes">
                                {displayBoolean(clientInfo.attestation_losses)}
                            </Field>
                            <Field icon={FiShield} label="Consentement traitement des données">
                                {displayBoolean(clientInfo.consent_data_processing)}
                            </Field>
                            <Field md={12} icon={FiInfo} label="Motif de différence Nom du titulaire du compte bancaire">
                                {displayReasonOfDifference(clientInfo)}
                            </Field>
                            <Field md={12} icon={FiInfo} label="Commentaire (autre motif)">
                                {displayOtherReasonComment(clientInfo)}
                            </Field>
                        </Row>
                    </ClientAccordionItem>
                </Accordion>
            </Card.Body>
        </Card>
    );
};

export default ClientInfoSections;
